import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/providers/AuthProvider';
import { useFriends } from '@/hooks/useFriends';
import { OnlineStatus } from './online-status';
import { 
  MessageSquare, 
  X, 
  Minus, 
  Users, 
  Search,
  Phone,
  Video,
  MoreHorizontal,
  Smile,
  Paperclip,
  Send,
  Settings
} from 'lucide-react';
import { FloatingChatWindow } from './floating-chat-window';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';

interface ChatContact {
  id: string;
  name: string;
  photoURL?: string;
  isOnline: boolean;
  lastMessage?: string;
  unreadCount: number;
  timestamp?: Date;
}

interface OpenChat {
  contactId: string;
  contact: ChatContact;
  isMinimized: boolean;
  position: number;
}

export function FloatingChatSystem() {
  const { user } = useAuth();
  const { friends } = useFriends();
  const { toast } = useToast();
  
  const [isMainPanelOpen, setIsMainPanelOpen] = useState(false);
  const [openChats, setOpenChats] = useState<OpenChat[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [contacts, setContacts] = useState<ChatContact[]>([]);

  // Convert friends to chat contacts
  useEffect(() => {
    const chatContacts: ChatContact[] = friends.map(friend => ({
      id: friend.otherUser?.id || friend.id,
      name: friend.otherUser?.name || friend.name || 'Unknown User',
      photoURL: friend.otherUser?.photoURL || friend.photoURL,
      isOnline: Math.random() > 0.5, // Random online status for now
      lastMessage: "Start a conversation",
      unreadCount: 0,
      timestamp: new Date()
    }));
    setContacts(chatContacts);
  }, [friends]);

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openChat = (contact: ChatContact) => {
    const existingChat = openChats.find(chat => chat.contactId === contact.id);
    
    if (existingChat) {
      // If chat exists but is minimized, restore it
      if (existingChat.isMinimized) {
        setOpenChats(prev => prev.map(chat =>
          chat.contactId === contact.id
            ? { ...chat, isMinimized: false }
            : chat
        ));
      }
      return;
    }

    // Don't allow more than 4 open chats
    if (openChats.length >= 4) {
      toast({
        title: "Too many chats open",
        description: "Close some chats to open a new one",
        variant: "destructive"
      });
      return;
    }

    const newChat: OpenChat = {
      contactId: contact.id,
      contact,
      isMinimized: false,
      position: openChats.length
    };

    setOpenChats(prev => [...prev, newChat]);
    setIsMainPanelOpen(false);
  };

  const closeChat = (contactId: string) => {
    setOpenChats(prev => {
      const filtered = prev.filter(chat => chat.contactId !== contactId);
      // Reposition remaining chats
      return filtered.map((chat, index) => ({ ...chat, position: index }));
    });
  };

  const minimizeChat = (contactId: string) => {
    setOpenChats(prev => prev.map(chat =>
      chat.contactId === contactId
        ? { ...chat, isMinimized: true }
        : chat
    ));
  };

  const restoreChat = (contactId: string) => {
    setOpenChats(prev => prev.map(chat =>
      chat.contactId === contactId
        ? { ...chat, isMinimized: false }
        : chat
    ));
  };

  if (!user) return null;

  const totalUnreadMessages = contacts.reduce((sum, contact) => sum + contact.unreadCount, 0);

  return (
    <>
      {/* Main Chat Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={() => setIsMainPanelOpen(!isMainPanelOpen)}
          className={cn(
            "relative h-14 w-14 rounded-full shadow-2xl transition-all duration-300 hover:scale-105",
            "bg-gradient-to-r from-blue-600 via-blue-600 to-cyan-600",
            "hover:from-blue-700 hover:via-blue-700 hover:to-cyan-700",
            isMainPanelOpen && "rotate-180"
          )}
        >
          <MessageSquare className="h-6 w-6" />
          {totalUnreadMessages > 0 && (
            <Badge 
              className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 flex items-center justify-center bg-red-500 text-white text-xs font-bold animate-pulse"
            >
              {totalUnreadMessages > 99 ? '99+' : totalUnreadMessages}
            </Badge>
          )}
        </Button>
      </div>

      {/* Main Chat Panel */}
      {isMainPanelOpen && (
        <div className="fixed bottom-24 right-6 z-40 w-80 max-h-96 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 via-blue-600 to-cyan-600 p-4 text-white">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg">Messages</h3>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20 h-8 w-8 p-0"
                  onClick={() => setIsMainPanelOpen(false)}
                >
                  <Settings className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20 h-8 w-8 p-0"
                  onClick={() => setIsMainPanelOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Search */}
            <div className="mt-3 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/70" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/70 focus:bg-white/30 focus:outline-none"
              />
            </div>
          </div>

          {/* Contacts List */}
          <div className="max-h-80 overflow-y-auto">
            {filteredContacts.length === 0 ? (
              <div className="p-6 text-center">
                <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">No conversations found</p>
              </div>
            ) : (
              <div className="p-2">
                {filteredContacts.map((contact) => (
                  <button
                    key={contact.id}
                    onClick={() => openChat(contact)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                  >
                    <div className="relative">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={contact.photoURL} />
                        <AvatarFallback className="bg-gradient-to-r from-blue-500 to-blue-700 text-white">
                          {contact.name.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <OnlineStatus isOnline={contact.isOnline || false} size="sm" />
                    </div>
                    
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-gray-900 truncate">
                          {contact.name}
                        </p>
                        {contact.unreadCount > 0 && (
                          <Badge className="bg-blue-500 text-white text-xs h-5 w-5 rounded-full p-0 flex items-center justify-center">
                            {contact.unreadCount}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">
                        {contact.lastMessage}
                      </p>
                    </div>

                    {contact.isOnline && (
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Open Chat Windows */}
      {openChats.map((chat) => (
        <FloatingChatWindow
          key={chat.contactId}
          contact={chat.contact}
          isMinimized={chat.isMinimized}
          position={chat.position}
          onClose={() => closeChat(chat.contactId)}
          onMinimize={() => minimizeChat(chat.contactId)}
          onRestore={() => restoreChat(chat.contactId)}
        />
      ))}
    </>
  );
}